import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Building2, ShieldCheck, Star, Package, Eye, FileText, MapPin, Clock, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { adminMenu } from "@/config/menus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill, Stars, LoadingScreen, inr } from "@/components/common";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Section = ({ icon: Icon, title, children, extra }) => (
  <div className="bg-white border border-navy-200 rounded-xl p-5">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2"><Icon size={17} className="text-brand" /><h3 className="font-heading font-semibold text-navy-900">{title}</h3></div>
      {extra}
    </div>
    {children}
  </div>
);

const Field = ({ label, value }) => (
  <div><div className="text-xs text-navy-400">{label}</div><div className="text-sm text-navy-800 font-medium">{value || "—"}</div></div>
);

export default function AdminAgencyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [packages, setPackages] = useState([]);
  const [score, setScore] = useState("");
  const [notes, setNotes] = useState("");
  const [assignCode, setAssignCode] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/agencies/${id}/detail`).then((r) => {
      setData(r.data);
      setScore(String(r.data.agency.admin_verification_score ?? 0));
      setNotes(r.data.agency.verification_notes || "");
    }).catch(() => {});
  }, [id]);
  useEffect(() => { load(); api.get("/admin/packages").then((r) => setPackages(r.data.filter((p) => p.status === "ACTIVE"))).catch(() => {}); }, [load]);

  if (!data) return <DashboardShell menu={adminMenu} title="Agency"><LoadingScreen /></DashboardShell>;
  const a = data.agency;
  const ho = a.head_office || {};
  const cur = data.current_subscription;

  const saveScore = async () => {
    try { await api.post(`/admin/agencies/${id}/score`, { score: Number(score), notes }); toast.success("Verification score saved"); load(); }
    catch (e) { if (!e.isAuthError) toast.error(e?.response?.data?.detail || "Could not save score"); }
  };
  const setVerification = async (status) => {
    try { await api.post(`/admin/agencies/${id}/verify`, { status }); toast.success(`Verification → ${status}`); load(); }
    catch (e) { if (!e.isAuthError) toast.error("Could not update verification"); }
  };
  const assign = async () => {
    if (!assignCode) { toast.error("Select a package"); return; }
    try { await api.post(`/admin/agencies/${id}/assign-package`, { code: assignCode }); toast.success("Package assigned"); setAssignCode(""); load(); }
    catch (e) { if (!e.isAuthError) toast.error(e?.response?.data?.detail || "Could not assign package"); }
  };
  const setListing = async (patch) => {
    try { await api.post(`/admin/agencies/${id}/listing`, patch); toast.success("Listing updated"); load(); }
    catch (e) { if (!e.isAuthError) toast.error("Could not update listing"); }
  };

  return (
    <DashboardShell menu={adminMenu} title={a.name}>
      <button onClick={() => navigate("/admin/agencies")} className="flex items-center gap-1 text-sm text-navy-500 hover:text-navy-900 mb-4" data-testid="back-to-agencies"><ArrowLeft size={15} /> Back to Agencies</button>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h1 className="font-heading text-2xl font-bold text-navy-900" data-testid="agency-detail-name">{a.name}</h1>
        <StatusPill status={a.listing_state} />
        {a.is_listed ? <span className="text-xs text-emerald-600 font-semibold" data-testid="listing-live">● Live on marketplace</span>
          : <span className="text-xs text-amber-600 font-semibold" data-testid="listing-not-live">● Not visible publicly</span>}
      </div>

      {!a.is_listed && a.listing_reasons?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 flex items-start gap-2" data-testid="listing-reasons">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
          <div><div className="text-sm font-semibold text-amber-800">Why this agency is not visible:</div>
            <ul className="text-sm text-amber-700 list-disc ml-5 mt-1">{a.listing_reasons.map((r) => <li key={r}>{r}</li>)}</ul></div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <Section icon={Building2} title="Company Information">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <Field label="Agency Name" value={a.name} />
            <Field label="Owner Name" value={a.owner_name} />
            <Field label="Email" value={a.email} />
            <Field label="Phone" value={a.mobile} />
            <Field label="Address" value={ho.address} />
            <Field label="City" value={ho.city} />
            <Field label="State" value={ho.state} />
            <Field label="PIN Code" value={ho.pincode} />
            <Field label="Services" value={(a.services || []).join(", ")} />
            <Field label="Guards (M/F)" value={`${a.male_guards || 0} / ${a.female_guards || 0} · total ${a.total_guards || 0}`} />
          </div>
        </Section>

        <Section icon={MapPin} title="City & Service Areas">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <Field label="Primary City" value={ho.city} />
            <Field label="State" value={ho.state} />
            <Field label="PIN Code" value={ho.pincode} />
            <Field label="Service Areas" value={(a.service_locations || []).join(", ")} />
            <Field label="Service PIN Codes" value={(a.service_pincodes || []).join(", ")} />
          </div>
        </Section>

        <Section icon={ShieldCheck} title="Verification & Admin Score"
          extra={<StatusPill status={a.verification_status} />}>
          <div className="flex gap-2 mb-4 flex-wrap">
            {["VERIFIED", "UNDER_REVIEW", "PENDING", "REJECTED", "EXPIRED"].map((s) => (
              <Button key={s} size="sm" variant={a.verification_status === s ? "default" : "outline"}
                className={a.verification_status === s ? "bg-brand" : "border-navy-300"} onClick={() => setVerification(s)} data-testid={`verify-${s}`}>{s.replace(/_/g, " ")}</Button>
            ))}
          </div>
          <Label className="text-xs">Admin Verification Score (0–5) — internal, separate from customer rating</Label>
          <div className="flex items-center gap-3 mt-1">
            <Input type="number" min="0" max="5" step="0.5" value={score} onChange={(e) => setScore(e.target.value)} className="w-24" data-testid="admin-score-input" />
            <Stars value={Number(score) || 0} />
          </div>
          <Label className="text-xs mt-3 block">Verification Notes</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="verification-notes" />
          <Button className="mt-3 bg-brand hover:bg-brand-600" size="sm" onClick={saveScore} data-testid="save-score-btn">Save Score & Notes</Button>
          {a.scored_at && <p className="text-xs text-navy-400 mt-2">Last scored {new Date(a.scored_at).toLocaleString("en-IN")}</p>}
        </Section>

        <Section icon={Star} title="Customer Rating (independent)">
          <div className="flex items-center gap-3">
            <span className="font-heading text-3xl font-bold text-navy-900" data-testid="customer-rating">{(a.rating || 0).toFixed(1)}</span>
            <div><Stars value={a.rating || 0} /><div className="text-xs text-navy-500 mt-1" data-testid="reviews-count">{data.agency.reviews_count || 0} customer reviews</div></div>
          </div>
          <p className="text-xs text-navy-400 mt-3">Customer rating comes from verified reviews only and is never changed by the admin score.</p>
        </Section>

        <Section icon={Package} title="Package & Subscription">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-4">
            <Field label="Current Package" value={a.subscription_plan} />
            <Field label="Subscription Status" value={a.subscription_status} />
            <Field label="Current Price" value={a.current_package_price != null ? inr(a.current_package_price) : "—"} />
            <Field label="Start Date" value={cur?.start_date ? new Date(cur.start_date).toLocaleDateString("en-IN") : "—"} />
            <Field label="Expiry Date" value={cur?.end_date ? new Date(cur.end_date).toLocaleDateString("en-IN") : "—"} />
            <Field label="Reference" value={cur?.reference} />
          </div>
          <Label className="text-xs">Assign / Change Package</Label>
          <div className="flex items-center gap-2 mt-1">
            <Select value={assignCode} onValueChange={setAssignCode}>
              <SelectTrigger className="flex-1" data-testid="assign-package-select"><SelectValue placeholder="Select active package…" /></SelectTrigger>
              <SelectContent>{packages.map((p) => <SelectItem key={p.code} value={p.code}>{p.name} — {p.price === 0 ? "Free" : inr(p.price)}/{p.billing_period}</SelectItem>)}</SelectContent>
            </Select>
            <Button className="bg-brand hover:bg-brand-600" onClick={assign} data-testid="assign-package-btn">Assign</Button>
          </div>
          {data.subscription_history?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs text-navy-400 mb-1">Assignment History (original price snapshots preserved)</div>
              <div className="border border-navy-200 rounded-lg divide-y divide-navy-100 max-h-40 overflow-y-auto" data-testid="subscription-history">
                {data.subscription_history.map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-2 text-xs">
                    <span className="text-navy-700">{s.package_name} · {s.price === 0 ? "Free" : inr(s.price)}</span>
                    <span className="text-navy-400">{new Date(s.assigned_at).toLocaleDateString("en-IN")} <StatusPill status={s.status} /></span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        <Section icon={Eye} title="Marketplace Listing Control">
          <div className="space-y-3">
            <div className="flex items-center justify-between"><Label className="text-sm">Marketplace Listing</Label><Switch checked={a.listing_enabled !== false} onCheckedChange={(v) => setListing({ listing_enabled: v })} data-testid="toggle-listing" /></div>
            <div className="flex items-center justify-between"><Label className="text-sm">Featured (separate from package)</Label><Switch checked={!!a.featured} onCheckedChange={(v) => setListing({ featured: v })} data-testid="toggle-featured" /></div>
            <div className="flex items-center justify-between"><Label className="text-sm">Suspended</Label><Switch checked={!!a.suspended} onCheckedChange={(v) => setListing({ suspended: v })} data-testid="toggle-suspended" /></div>
            <div className="flex items-center justify-between"><Label className="text-sm">Subscription Active</Label><Switch checked={(a.subscription_status || "ACTIVE") === "ACTIVE"} onCheckedChange={(v) => setListing({ subscription_status: v ? "ACTIVE" : "EXPIRED" })} data-testid="toggle-subscription" /></div>
          </div>
          <a href={`${BACKEND.replace(/\/$/, "")}`.replace(/\/api$/, "") + `/agencies/${a.slug || a.id}`} target="_blank" rel="noreferrer" className="inline-block mt-4 text-sm text-brand hover:underline" data-testid="public-profile-link">View public profile →</a>
        </Section>

        <Section icon={FileText} title="Verification Documents">
          {data.documents.length === 0 ? <p className="text-sm text-navy-500">No documents uploaded.</p> : (
            <div className="border border-navy-200 rounded-lg divide-y divide-navy-100" data-testid="documents-list">
              {data.documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-3 p-3" data-testid={`doc-${d.id}`}>
                  <div className="flex items-center gap-2 text-sm text-navy-800 min-w-0"><FileText size={15} className="text-brand shrink-0" /><span className="truncate">{d.doc_type}</span></div>
                  <div className="flex items-center gap-2 shrink-0"><StatusPill status={d.status} />
                    {d.view_url ? <a href={`${BACKEND}${d.view_url}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="border-navy-300" data-testid={`doc-open-${d.id}`}><Eye size={14} className="mr-1" /> Open</Button></a> : <span className="text-xs text-navy-400">No file</span>}</div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section icon={Clock} title="Activity History">
          {data.activity?.length === 0 ? <p className="text-sm text-navy-500">No recorded activity yet.</p> : (
            <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="activity-log">
              {data.activity.map((ev) => (
                <div key={ev.id} className="text-xs border-l-2 border-brand/40 pl-3 py-0.5">
                  <span className="font-semibold text-navy-800">{ev.action.replace(/_/g, " ")}</span>
                  <span className="text-navy-400"> · {new Date(ev.timestamp).toLocaleString("en-IN")}</span>
                  {ev.meta?.from != null && <div className="text-navy-500">{String(ev.meta.from)} → {String(ev.meta.to)}</div>}
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </DashboardShell>
  );
}
